import { useEffect, useState } from "react";
import { ArrowRight, Check, CreditCard, FileText, X, Loader2, Tag, Sparkles } from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { type CartItem, type OrderMode, getExactTier, getPrice } from "@/data/pricing";
import { supabase } from "@/integrations/supabase/client";
import { stripeEnvironment } from "@/lib/stripe";
import StripeEmbeddedCheckout from "@/components/StripeEmbeddedCheckout";
import { useCheckout } from "@/contexts/CheckoutContext";
import { toast } from "sonner";

interface CheckoutSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cart: CartItem[];
  orderMode: OrderMode;
  totalPrice: number;
  onOrderModeChange?: (mode: OrderMode, totalPrice: number) => void;
  resumeShippingAddress?: {
    address: string;
    city: string;
    postalCode: string;
    country: string;
  } | null;
}

const inputClass =
  "w-full bg-white/50 backdrop-blur-sm border-0 rounded-lg px-5 py-4 text-sm tracking-widest uppercase placeholder:text-muted-foreground/70 placeholder:tracking-widest focus:outline-none focus:ring-2 focus:ring-ring/30 transition-all";

type Step = 1 | 2 | 3 | 4; // 1=Adress, 2=Metod, 3=Stripe Embedded, 4=Faktura skickad

const CheckoutSheet = ({ open, onOpenChange, cart, orderMode, totalPrice, onOrderModeChange, resumeShippingAddress }: CheckoutSheetProps) => {
  const { promoCode, clearPromoCode, resumeOrderId } = useCheckout();
  const [step, setStep] = useState<Step>(1);
  const [billingData, setBillingData] = useState({
    address: "",
    city: "",
    postalCode: "",
    country: "SE",
  });
  const [paymentMethod, setPaymentMethod] = useState<"card" | "invoice">("card");
  const [nickname, setNickname] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [invoiceMessage, setInvoiceMessage] = useState<string | null>(null);
  // The current draft order id, set when create-checkout returns. Used to
  // cancel the draft if the user closes the sheet without completing the
  // payment, so an abandoned row never lingers as a phantom order.
  const [activeOrderId, setActiveOrderId] = useState<string | null>(null);

  // ---- Billing details (only used when paymentMethod === "invoice") ----
  const [billingDetails, setBillingDetails] = useState({
    company_name: "",
    org_number: "",
    vat_number: "",
    contact_name: "",
    email: "",
    address: "",
    postal_code: "",
    city: "",
    country: "SE",
    reference: "",
  });
  const [billingLoaded, setBillingLoaded] = useState(false);

  // ---- Discount state ----
  const [discountInput, setDiscountInput] = useState("");
  const [discountOpen, setDiscountOpen] = useState(false);
  const [discountStatus, setDiscountStatus] = useState<
    | { state: "idle" }
    | { state: "checking" }
    | { state: "valid"; code: string; label: string; savingsCents: number; finalCents: number }
    | { state: "invalid"; message: string }
  >({ state: "idle" });

  // ---- VIP automatic discount (no code needed) ----
  const [vipDiscount, setVipDiscount] = useState<
    | { label: string; code: string | null; savingsCents: number; finalCents: number }
    | null
  >(null);

  const totalTubes = cart.reduce((s, c) => s + c.quantity, 0);
  const tier = getExactTier(totalTubes);
  const subtotalCents = totalPrice * 100;

  // Manual code wins only if it saves more than VIP, otherwise VIP applies.
  const manualSavings = discountStatus.state === "valid" ? discountStatus.savingsCents : 0;
  const vipSavings = vipDiscount?.savingsCents ?? 0;
  const useVip = vipSavings > manualSavings;
  const savingsCents = Math.max(manualSavings, vipSavings);
  const activeDiscountLabel = useVip
    ? vipDiscount?.label ?? null
    : discountStatus.state === "valid"
      ? discountStatus.label
      : null;
  const activeDiscountCode = useVip
    ? vipDiscount?.code ?? null
    : discountStatus.state === "valid"
      ? discountStatus.code
      : null;
  const finalCents = subtotalCents - savingsCents;
  const finalLabel = `${Math.round(finalCents / 100)} kr${orderMode === "subscription" ? "/mån" : ""}`;

  // Prices for both modes at the current tube count — used by the toggle so
  // the customer can see what each mode costs before switching.
  const purchasePrice = tier ? tier.purchasePrice : null;
  const subscriptionPrice = tier ? tier.subscriptionPrice : null;
  const modeSavings =
    purchasePrice != null && subscriptionPrice != null ? purchasePrice - subscriptionPrice : 0;

  // Mode is locked while resuming an existing order (it already exists at the
  // payment provider) and while the embedded payment step is open.
  const modeLocked = !!resumeOrderId || !onOrderModeChange || step >= 3;

  const switchMode = (mode: OrderMode) => {
    if (mode === orderMode || modeLocked) return;
    const nextPrice = getPrice(totalTubes, mode);
    if (nextPrice == null) return;
    // Drop any draft order created for the previous mode so it doesn't linger
    // as a phantom "finish payment" row.
    if (activeOrderId) {
      const orderToCancel = activeOrderId;
      supabase.functions
        .invoke("cancel-pending-order", { body: { orderId: orderToCancel } })
        .catch((err) => console.warn("cancel-pending-order failed", err));
      setActiveOrderId(null);
    }
    setClientSecret(null);
    setInvoiceMessage(null);
    setSubmitting(false);
    onOrderModeChange?.(mode, nextPrice);
  };


  const resetAndClose = () => {
    // If we created a draft order in this session and the customer didn't
    // reach the "faktura skickad" step (4) — they're abandoning. Cancel the
    // draft on the backend so it doesn't show up as a phantom "Slutför
    // betalning"-row in their account. Idempotent: edge function only flips
    // awaiting_payment/failed → cancelled, so a successful payment that
    // already updated the row to "paid" is unaffected.
    if (activeOrderId && step !== 4) {
      const orderToCancel = activeOrderId;
      // Fire-and-forget — UX shouldn't block on cleanup, and the backend
      // cleanup is idempotent in case of retries.
      supabase.functions
        .invoke("cancel-pending-order", { body: { orderId: orderToCancel } })
        .catch((err) => console.warn("cancel-pending-order failed", err));
    }
    setActiveOrderId(null);
    setStep(1);
    setClientSecret(null);
    setInvoiceMessage(null);
    setSubmitting(false);
    setDiscountInput("");
    setDiscountOpen(false);
    setDiscountStatus({ state: "idle" });
    setVipDiscount(null);
    onOpenChange(false);
  };

  // Debounced live validation of discount code
  useEffect(() => {
    const code = discountInput.trim().toUpperCase();
    if (!code) {
      setDiscountStatus({ state: "idle" });
      return;
    }
    if (!tier) return;
    setDiscountStatus({ state: "checking" });
    const t = setTimeout(async () => {
      try {
        const { data, error } = await supabase.functions.invoke("validate-discount", {
          body: { code, tierTubes: totalTubes, mode: orderMode },
        });
        if (error) {
          setDiscountStatus({ state: "invalid", message: "Kunde inte validera koden" });
          return;
        }
        if (data?.valid) {
          setDiscountStatus({
            state: "valid",
            code,
            label: data.label,
            savingsCents: data.savingsCents,
            finalCents: data.finalCents,
          });
        } else {
          setDiscountStatus({ state: "invalid", message: data?.message || "Ogiltig kod" });
        }
      } catch (err) {
        console.error(err);
        setDiscountStatus({ state: "invalid", message: "Fel vid validering" });
      }
    }, 400);
    return () => clearTimeout(t);
  }, [discountInput, totalTubes, orderMode, tier]);

  // Prefill discount code from ?promo= URL param (via CheckoutContext)
  useEffect(() => {
    if (!open) return;
    if (promoCode && !discountInput) {
      setDiscountOpen(true);
      setDiscountInput(promoCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, promoCode]);

  // Prefill billing address when resuming an awaiting_payment order, and
  // jump straight to the payment-method step (address is already known).
  useEffect(() => {
    if (!open) return;
    if (resumeShippingAddress && resumeShippingAddress.address) {
      setBillingData({
        address: resumeShippingAddress.address || "",
        city: resumeShippingAddress.city || "",
        postalCode: resumeShippingAddress.postalCode || "",
        country: resumeShippingAddress.country || "SE",
      });
      setStep(2);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, resumeShippingAddress]);

  // Auto-fetch VIP discount when sheet opens or tier/mode changes
  useEffect(() => {
    if (!open || !tier) {
      setVipDiscount(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke("get-active-discount", {
          body: { tierTubes: totalTubes, mode: orderMode },
        });
        if (cancelled) return;
        if (error || !data?.vip) {
          setVipDiscount(null);
          return;
        }
        setVipDiscount({
          label: data.vip.label,
          code: data.vip.code,
          savingsCents: data.vip.savingsCents,
          finalCents: data.vip.finalCents,
        });
      } catch (err) {
        console.error("VIP discount fetch failed", err);
        if (!cancelled) setVipDiscount(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, tier, totalTubes, orderMode]);

  // Prefill billing_details from DB (and profile fallback) when sheet opens
  useEffect(() => {
    if (!open || billingLoaded) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: auth } = await supabase.auth.getUser();
        const user = auth?.user;
        if (!user) return;
        const { data: bd } = await supabase
          .from("billing_details")
          .select("*")
          .eq("user_id", user.id)
          .maybeSingle();
        if (cancelled) return;
        if (bd) {
          setBillingDetails({
            company_name: bd.company_name ?? "",
            org_number: bd.org_number ?? "",
            vat_number: bd.vat_number ?? "",
            contact_name: bd.contact_name ?? "",
            email: bd.email ?? user.email ?? "",
            address: bd.address ?? "",
            postal_code: bd.postal_code ?? "",
            city: bd.city ?? "",
            country: bd.country ?? "SE",
            reference: bd.reference ?? "",
          });
        } else {
          // Fall back to profile basics
          const { data: profile } = await supabase
            .from("profiles")
            .select("first_name, last_name, company, email")
            .eq("id", user.id)
            .maybeSingle();
          if (cancelled) return;
          setBillingDetails((prev) => ({
            ...prev,
            company_name: profile?.company ?? prev.company_name,
            contact_name:
              [profile?.first_name, profile?.last_name].filter(Boolean).join(" ") ||
              prev.contact_name,
            email: profile?.email ?? user.email ?? prev.email,
          }));
        }
      } catch (err) {
        console.warn("billing_details prefill failed", err);
      } finally {
        if (!cancelled) setBillingLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, billingLoaded]);

  const addressValid = billingData.address && billingData.city && billingData.postalCode;
  const billingValid =
    !!billingDetails.company_name.trim() &&
    !!billingDetails.address.trim() &&
    !!billingDetails.postal_code.trim() &&
    !!billingDetails.city.trim() &&
    !!billingDetails.country.trim();


  const startCheckout = async () => {
    if (submitting) return;
    if (!tier) {
      toast.error("Kunde inte hitta paketet. Välj en exakt tier.");
      return;
    }

    setSubmitting(true);
    try {
      const priceId = orderMode === "subscription" ? tier.subscriptionPriceId : tier.purchasePriceId;
      const quantities: Record<string, number> = {};
      cart.forEach((c) => {
        quantities[c.product.id] = c.quantity;
      });

      const returnUrl = `${window.location.origin}/order/return?session_id={CHECKOUT_SESSION_ID}`;

      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: {
          priceId,
          tierTubes: totalTubes,
          mode: orderMode,
          quantities,
          paymentMethod,
          shippingAddress: billingData,
          returnUrl,
          environment: stripeEnvironment,
          ...(resumeOrderId && { resumeOrderId }),
          ...(nickname.trim() && { nickname: nickname.trim() }),
          ...(discountStatus.state === "valid" && { discountCode: discountStatus.code }),
          ...(paymentMethod === "invoice" && {
            billingDetails: {
              company_name: billingDetails.company_name.trim(),
              org_number: billingDetails.org_number.trim() || null,
              vat_number: billingDetails.vat_number.trim() || null,
              contact_name: billingDetails.contact_name.trim() || null,
              email: billingDetails.email.trim() || null,
              address: billingDetails.address.trim(),
              postal_code: billingDetails.postal_code.trim(),
              city: billingDetails.city.trim(),
              country: billingDetails.country.trim().toUpperCase(),
              reference: billingDetails.reference.trim() || null,
            },
          }),
        },
      });

      // Edge function may return 4xx/5xx with a JSON body { error: "..." }.
      // supabase-js folds that into `error` but loses the body — pull the
      // friendlier message out of `data` when present so the toast shows
      // the real reason instead of "non-2xx status code".
      const friendlyError =
        (data && typeof data === "object" && "error" in data && typeof data.error === "string"
          ? data.error
          : null) ||
        error?.message ||
        "Kunde inte starta betalning. Försök igen.";

      if (error || (data && typeof data === "object" && "error" in data)) {
        console.error("create-checkout error", error || data);
        toast.error(friendlyError);
        return;
      }

      // Track the draft order id so we can cancel it if the user closes
      // the sheet without completing the payment.
      if (data?.orderId) setActiveOrderId(data.orderId);

      if (data.flow === "embedded") {
        setClientSecret(data.clientSecret);
        setStep(3);
      } else if (data.flow === "invoice") {
        setInvoiceMessage(data.message || "Faktura skickas till din e-post inom kort.");
        setStep(4);
      }
    } catch (err) {
      console.error("Order failed", err);
      toast.error("Kunde inte slutföra beställningen. Försök igen.");
    } finally {
      setSubmitting(false);
    }
  };

  if (cart.length === 0) return null;

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) resetAndClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-[50vw] p-0 border-0 [&>button]:hidden overflow-y-auto">
        {step === 4 ? (
          // ============ Faktura skickad ============
          <div className="h-full flex flex-col items-center justify-center bg-gradient-to-b from-coral-400/30 via-terracotta-100/40 to-cream-100 px-8 text-center">
            <span className="font-serif italic text-5xl md:text-6xl text-foreground">Tack</span>
            <h2 className="text-2xl md:text-3xl font-bold uppercase tracking-wider text-foreground mt-2">
              för din beställning
            </h2>
            <p className="text-sm text-muted-foreground mt-4 max-w-sm">
              {invoiceMessage}
            </p>
            <p className="text-xs text-muted-foreground mt-2 max-w-sm">
              Du kan följa beställningen på din kontosida.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-10 w-full max-w-sm">
              <button
                onClick={resetAndClose}
                className="flex-1 border border-foreground text-foreground bg-transparent py-3.5 rounded-lg font-medium uppercase tracking-widest text-xs hover:bg-foreground hover:text-background transition-all duration-300 inline-flex items-center justify-center gap-2"
              >
                Stäng <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ) : step === 3 && clientSecret ? (
          // ============ Stripe Embedded Checkout ============
          <div className="h-full flex flex-col bg-cream-50 overflow-y-auto">
            <div className="p-6 md:p-8 pb-2 flex items-center justify-between">
              <div>
                <span className="font-serif italic text-2xl text-foreground">Säker</span>
                <h2 className="text-lg font-bold uppercase tracking-wider text-foreground">Betalning</h2>
              </div>
              <button
                onClick={resetAndClose}
                className="p-2 rounded-full hover:bg-white/50 transition-colors"
                aria-label="Stäng"
              >
                <X className="w-5 h-5 text-foreground" />
              </button>
            </div>
            <div className="flex-1 px-2 pb-6">
              <StripeEmbeddedCheckout clientSecret={clientSecret} />
            </div>
          </div>
        ) : (
          // ============ Steg 1 + 2: Adress + metod ============
          <div className="h-full flex flex-col bg-gradient-to-b from-coral-400/20 via-cream-100 to-cream-50 overflow-y-auto">
            <div className="p-8 md:p-10 pb-0">
              <button
                onClick={resetAndClose}
                className="p-2 rounded-full hover:bg-white/30 transition-colors -ml-2 mb-4"
                aria-label="Stäng"
              >
                <X className="w-5 h-5 text-foreground" />
              </button>
              <span className="font-serif italic text-3xl md:text-4xl text-foreground">Din</span>
              <h2 className="text-xl md:text-2xl font-bold uppercase tracking-wider text-foreground">Beställning</h2>

              {/* Välj läge: engångsköp eller abonnemang — byts direkt i kassan */}
              <div className="mt-4 rounded-xl bg-white/60 border border-foreground/10 p-3">
                <div className="grid grid-cols-2 gap-2">
                  {(["purchase", "subscription"] as OrderMode[]).map((m) => {
                    const active = orderMode === m;
                    const price = m === "purchase" ? purchasePrice : subscriptionPrice;
                    return (
                      <button
                        key={m}
                        type="button"
                        onClick={() => switchMode(m)}
                        disabled={modeLocked && !active}
                        aria-pressed={active}
                        className={`rounded-lg px-3 py-3 text-left transition-all border ${
                          active
                            ? "bg-foreground text-background border-foreground"
                            : "bg-white/60 text-foreground border-foreground/10 hover:border-foreground/40 disabled:opacity-50 disabled:cursor-not-allowed"
                        }`}
                      >
                        <span className="block text-[11px] font-bold uppercase tracking-widest">
                          {m === "purchase" ? "Engångsköp" : "Abonnemang"}
                        </span>
                        <span className={`block text-[11px] mt-1 ${active ? "text-background/80" : "text-muted-foreground"}`}>
                          {price != null
                            ? m === "purchase"
                              ? `${price} kr en gång`
                              : `${price} kr/mån`
                            : m === "purchase"
                              ? "En betalning"
                              : "Varje månad"}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-muted-foreground mt-2">
                  {orderMode === "subscription"
                    ? "Betalas varje månad — leverans och betalning återkommer tills du avslutar."
                    : modeSavings > 0
                      ? `En leverans, en betalning. Med abonnemang betalar du ${modeSavings} kr mindre varje månad.`
                      : "En leverans, en betalning."}
                </p>
                {modeLocked && (
                  <p className="text-[10px] text-muted-foreground mt-1 italic">
                    Läget kan inte ändras för den här beställningen.
                  </p>
                )}
              </div>



              {/* Cart summary */}
              <div className="mt-6 space-y-2">
                {cart.map((item) => (
                  <div key={item.product.id} className="flex items-center gap-2">
                    <span className="bg-foreground text-background text-[10px] font-bold w-5 h-5 rounded-full inline-flex items-center justify-center">
                      {item.quantity}
                    </span>
                    <p className="text-xs font-semibold uppercase tracking-wider text-foreground">
                      {item.product.name}
                    </p>
                  </div>
                ))}
                <p className="text-[10px] text-muted-foreground mt-1 ml-7 uppercase tracking-wider">
                  Dispenser ingår • {totalTubes} tuber totalt
                </p>
              </div>
            </div>

            <div className="flex-1 p-8 md:p-10 pt-6">
              {step === 1 ? (
                <div className="space-y-6">
                  <div>
                    <span className="font-serif italic text-2xl text-foreground">Leverans</span>
                    <h3 className="text-lg font-bold uppercase tracking-wider text-foreground">Adress</h3>
                    <p className="text-xs text-muted-foreground mt-1">Leverans sker inom 5–7 arbetsdagar.</p>
                  </div>
                  <div className="space-y-3">
                    <input type="text" placeholder="ADRESS" value={billingData.address} onChange={(e) => setBillingData({ ...billingData, address: e.target.value })} className={inputClass} />
                    <div className="grid grid-cols-2 gap-3">
                      <input type="text" placeholder="ORT" value={billingData.city} onChange={(e) => setBillingData({ ...billingData, city: e.target.value })} className={inputClass} />
                      <input type="text" placeholder="POSTNUMMER" value={billingData.postalCode} onChange={(e) => setBillingData({ ...billingData, postalCode: e.target.value })} className={inputClass} />
                    </div>
                    <select value={billingData.country} onChange={(e) => setBillingData({ ...billingData, country: e.target.value })} className={inputClass}>
                      <option value="SE">SVERIGE</option>
                      <option value="NO">NORGE</option>
                      <option value="DK">DANMARK</option>
                      <option value="FI">FINLAND</option>
                    </select>
                    {orderMode === "subscription" && (
                      <input
                        type="text"
                        placeholder="NAMN PÅ ANLÄGGNING (VALFRITT)"
                        value={nickname}
                        onChange={(e) => setNickname(e.target.value)}
                        maxLength={80}
                        className={inputClass}
                      />
                    )}
                  </div>
                  <button
                    onClick={() => setStep(2)}
                    disabled={!addressValid}
                    className="w-full border border-foreground text-foreground bg-transparent py-3.5 rounded-lg font-medium uppercase tracking-widest text-xs hover:bg-foreground hover:text-background transition-all duration-300 inline-flex items-center justify-center gap-2 mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Välj betalningsmetod <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-primary" />
                      <span className="text-xs uppercase tracking-wider text-foreground font-medium">Leveransadress</span>
                    </div>
                    <button onClick={() => setStep(1)} className="text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors">
                      Ändra
                    </button>
                  </div>

                  <div>
                    <span className="font-serif italic text-2xl text-foreground">Betalnings</span>
                    <h3 className="text-lg font-bold uppercase tracking-wider text-foreground">Metod</h3>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => setPaymentMethod("card")}
                      className={`flex-1 py-3 rounded-lg text-xs font-semibold uppercase tracking-widest transition-all ${paymentMethod === "card" ? "bg-foreground text-background" : "bg-white/50 text-foreground hover:bg-white/70"}`}
                    >
                      <CreditCard className="w-4 h-4 inline mr-2" />
                      Kortbetalning
                    </button>
                    <button
                      onClick={() => setPaymentMethod("invoice")}
                      className={`flex-1 py-3 rounded-lg text-xs font-semibold uppercase tracking-widest transition-all ${paymentMethod === "invoice" ? "bg-foreground text-background" : "bg-white/50 text-foreground hover:bg-white/70"}`}
                    >
                      <FileText className="w-4 h-4 inline mr-2" />
                      Faktura
                    </button>
                  </div>

                  <div className="bg-white/40 rounded-lg p-5 text-xs text-muted-foreground leading-relaxed">
                    {paymentMethod === "card" ? (
                      <>
                        Du betalar säkert med kort via vår betalpartner. Inga kortuppgifter sparas hos oss.
                        {orderMode === "subscription" && (
                          <p className="mt-2">Beloppet dras automatiskt varje månad. Du kan avsluta när som helst.</p>
                        )}
                      </>
                    ) : (
                      <>
                        En faktura skickas till din e-post med 30 dagars betalningsvillkor.
                        {orderMode === "subscription" && (
                          <p className="mt-2">En ny faktura skapas automatiskt vid varje månadsleverans.</p>
                        )}
                      </>
                    )}
                  </div>

                  {/* Faktureringsuppgifter — krävs för faktura */}
                  {paymentMethod === "invoice" && (
                    <div className="space-y-3">
                      <div>
                        <span className="font-serif italic text-xl text-foreground">Fakturerings</span>
                        <h4 className="text-sm font-bold uppercase tracking-wider text-foreground">Uppgifter</h4>
                        <p className="text-[11px] text-muted-foreground mt-1">
                          Sparas på ditt konto till nästa gång.
                        </p>
                      </div>
                      <input
                        type="text"
                        placeholder="FÖRETAGSNAMN *"
                        value={billingDetails.company_name}
                        onChange={(e) => setBillingDetails({ ...billingDetails, company_name: e.target.value })}
                        className={inputClass}
                      />
                      <div className="grid grid-cols-2 gap-3">
                        <input
                          type="text"
                          placeholder="ORG.NR"
                          value={billingDetails.org_number}
                          onChange={(e) => setBillingDetails({ ...billingDetails, org_number: e.target.value })}
                          className={inputClass}
                        />
                        <input
                          type="text"
                          placeholder="VAT-NR (EU)"
                          value={billingDetails.vat_number}
                          onChange={(e) => setBillingDetails({ ...billingDetails, vat_number: e.target.value })}
                          className={inputClass}
                        />
                      </div>
                      <input
                        type="text"
                        placeholder="KONTAKTPERSON"
                        value={billingDetails.contact_name}
                        onChange={(e) => setBillingDetails({ ...billingDetails, contact_name: e.target.value })}
                        className={inputClass}
                      />
                      <input
                        type="email"
                        placeholder="FAKTURA-E-POST"
                        value={billingDetails.email}
                        onChange={(e) => setBillingDetails({ ...billingDetails, email: e.target.value })}
                        className={inputClass}
                      />
                      <input
                        type="text"
                        placeholder="FAKTURERINGSADRESS *"
                        value={billingDetails.address}
                        onChange={(e) => setBillingDetails({ ...billingDetails, address: e.target.value })}
                        className={inputClass}
                      />
                      <div className="grid grid-cols-2 gap-3">
                        <input
                          type="text"
                          placeholder="ORT *"
                          value={billingDetails.city}
                          onChange={(e) => setBillingDetails({ ...billingDetails, city: e.target.value })}
                          className={inputClass}
                        />
                        <input
                          type="text"
                          placeholder="POSTNUMMER *"
                          value={billingDetails.postal_code}
                          onChange={(e) => setBillingDetails({ ...billingDetails, postal_code: e.target.value })}
                          className={inputClass}
                        />
                      </div>
                      <select
                        value={billingDetails.country}
                        onChange={(e) => setBillingDetails({ ...billingDetails, country: e.target.value })}
                        className={inputClass}
                      >
                        <option value="SE">SVERIGE</option>
                        <option value="NO">NORGE</option>
                        <option value="DK">DANMARK</option>
                        <option value="FI">FINLAND</option>
                      </select>
                      <input
                        type="text"
                        placeholder="ER REFERENS / KOSTNADSSTÄLLE"
                        value={billingDetails.reference}
                        onChange={(e) => setBillingDetails({ ...billingDetails, reference: e.target.value })}
                        className={inputClass}
                      />
                    </div>
                  )}

                  {/* VIP automatic discount banner */}
                  {vipDiscount && (
                    <div className="bg-gradient-to-r from-coral-400/30 to-terracotta-100/50 rounded-lg p-4 border border-coral-400/30">
                      <div className="flex items-start gap-2">
                        <Sparkles className="w-4 h-4 text-foreground mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-xs font-semibold uppercase tracking-widest text-foreground">
                            VIP-rabatt aktiverad
                          </p>
                          <p className="text-[11px] text-foreground/80 mt-1">
                            {vipDiscount.label} — du sparar {Math.round(vipDiscount.savingsCents / 100)} kr automatiskt
                          </p>
                          {useVip && discountStatus.state === "valid" && (
                            <p className="text-[10px] text-muted-foreground mt-1 italic">
                              (Din VIP-rabatt är bättre än koden {discountStatus.code})
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Discount code */}
                  <div className="bg-white/40 rounded-lg p-4">
                    {!discountOpen && discountStatus.state !== "valid" ? (
                      <button
                        type="button"
                        onClick={() => setDiscountOpen(true)}
                        className="text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground inline-flex items-center gap-2"
                      >
                        <Tag className="w-3.5 h-3.5" /> Har du en rabattkod?
                      </button>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={discountInput}
                            onChange={(e) => setDiscountInput(e.target.value.toUpperCase())}
                            placeholder="RABATTKOD"
                            className={inputClass + " flex-1"}
                          />
                          {discountStatus.state === "valid" && (
                            <button
                              type="button"
                              onClick={() => {
                                setDiscountInput("");
                                setDiscountStatus({ state: "idle" });
                                clearPromoCode();
                              }}
                              className="text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground px-2"
                            >
                              Ta bort
                            </button>
                          )}
                        </div>
                        {discountStatus.state === "checking" && (
                          <p className="text-[11px] text-muted-foreground"><Loader2 className="w-3 h-3 inline animate-spin mr-1" />Kontrollerar...</p>
                        )}
                        {discountStatus.state === "valid" && (
                          <p className="text-[11px] text-emerald-700 font-medium">
                            ✓ {discountStatus.label} — du sparar {Math.round(discountStatus.savingsCents / 100)} kr
                          </p>
                        )}
                        {discountStatus.state === "invalid" && (
                          <p className="text-[11px] text-destructive">{discountStatus.message}</p>
                        )}
                      </div>
                    )}
                  </div>

                  <button
                    onClick={startCheckout}
                    disabled={submitting || (paymentMethod === "invoice" && !billingValid)}
                    className="w-full bg-foreground text-background py-3.5 rounded-lg font-medium uppercase tracking-widest text-xs hover:bg-foreground/90 transition-all duration-300 inline-flex items-center justify-center gap-2 mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <>{paymentMethod === "card"
                          ? orderMode === "subscription" ? "Starta abonnemang" : "Fortsätt till betalning"
                          : orderMode === "subscription" ? "Starta abonnemang med faktura" : "Skicka faktura"} <ArrowRight className="w-3.5 h-3.5" /></>

                    }
                  </button>
                </div>
              )}
            </div>

            <div className="border-t border-foreground/10 p-8 md:px-10 py-4 space-y-1">
              {savingsCents > 0 && (
                <>
                  <div className="flex justify-between items-center text-xs text-muted-foreground">
                    <span>Subtotal</span>
                    <span>{Math.round(subtotalCents / 100)} kr</span>
                  </div>
                  <div className="flex justify-between items-center text-xs text-emerald-700">
                    <span>
                      Rabatt {activeDiscountCode ? `(${activeDiscountCode})` : activeDiscountLabel ? `(${activeDiscountLabel})` : ""}
                    </span>
                    <span>−{Math.round(savingsCents / 100)} kr</span>
                  </div>
                </>
              )}
              <div className="flex justify-between items-center pt-1">
                <span className="text-xs font-semibold uppercase tracking-wider text-foreground">
                  {orderMode === "subscription" ? "Att betala / mån" : "Att betala"}
                </span>
                <span className="text-lg font-bold text-foreground">{finalLabel}</span>
              </div>
              {orderMode === "subscription" && (
                <p className="text-[10px] text-muted-foreground pt-1">
                  Dras varje månad tills du avslutar. Du kan pausa eller avsluta när du vill.
                </p>
              )}

            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CheckoutSheet;
